//@ts-nocheck
import { ConfigProvider, DatePicker, DatePickerProps } from 'antd';
import './components.scss';
import dayjs, { Dayjs } from 'dayjs';
import cn from 'classnames';
import Label from './Label';
import HelperText from './HelperText';
import locale from 'antd/locale/ar_EG';
import 'dayjs/locale/ar';
// import { useLocale } from 'next-intl';
import { inputBaseClasses } from './Input';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import arEG from 'antd/es/locale/ar_EG'; // Ant Design Arabic locale
import enUS from 'antd/es/locale/en_US';

type Props = {
  id: string;
  status?: 'error' | 'success';
  disabled?: boolean;
  helperText?: string;
  label?: string;
  required?: boolean;
  labelHelperText?: string;
  isRange?: boolean;
  customDisabledDate?: (current: Dayjs) => boolean;
  disabledDateCustom?: (current: Dayjs) => boolean;
  disabledDate?: (current: Dayjs) => boolean;
  className?: string;
  labelClassName?: string;
} & Omit<React.ComponentProps<typeof DatePicker>, 'status' | 'disabledDate'>;

const { RangePicker } = DatePicker;

const CustomDatePicker = forwardRef<HTMLInputElement, Props>(
  (
    {
      status,
      disabled,
      id,
      helperText,
      label,
      required,
      labelHelperText,
      isRange,
      customDisabledDate,
      disabledDateCustom,
      disabledDate: propDisabledDate,
      className,
      labelClassName,
      ...props
    }: Props,
    ref
  ) => {
    // const currentLang = useLocale();
    const {
      t,
      i18n: { language },
    } = useTranslation();

    const customCell: DatePickerProps<Dayjs>['cellRender'] = (
      current,
      info
    ) => {
      if (info.type !== 'date') {
        return info.originNode;
      }

      if (typeof current === 'number' || typeof current === 'string') {
        return (
          <div className="ant-picker-cell-inner !w-6 !h-6 !flex !items-center !justify-center !rounded-full !text-xs">
            {current}
          </div>
        );
      }
      return (
        <div className="ant-picker-cell-inner !w-6 !h-6 !flex !items-center !justify-center !rounded-full !text-xs">
          {current.date()}
        </div>
      );
    };

    const disableBackYears = (current: Dayjs | null) => {
      // Disable dates that are more than 50 years back
      return (
        current &&
        current.isBefore(dayjs().subtract(100, 'years').startOf('day'))
      );
    };

    const resolvedDisabledDate = (current: Dayjs) => {
      if (customDisabledDate) {
        return Boolean(customDisabledDate(current));
      }
      if (disabledDateCustom) {
        return Boolean(disabledDateCustom(current));
      }
      if (propDisabledDate) {
        return Boolean(propDisabledDate(current));
      }
      return Boolean(disableBackYears(current));
    };

    dayjs.locale(language);

    return (
      <div className="our-date-picker flex flex-col">
        {label && (
          <Label
            labelHelperText={labelHelperText}
            required={required}
            htmlFor={id}
            text={label}
            className={labelClassName}
          />
        )}
        <ConfigProvider
          locale={language === 'ar' ? arEG : enUS}
          direction={language === 'ar' ? 'rtl' : 'ltr'}
        >
          {isRange ? (
            <RangePicker
              format={(value) =>
                dayjs(value).locale(language).format('DD MMMM YYYY')
              }
              placeholder={[t('home.startDate'), t('home.endDate')]}
              id={id}
              disabledDate={resolvedDisabledDate}
              {...props}
              // locale={language}
              size="large"
              className={cn(`${inputBaseClasses} ${className}`, {
                '!border-red-500  ': status === 'error',
                '!border-green-500 ': status === 'success',
                'border-0 dark:border dark:!border-dark-text-placeholder !bg-light-button-label-primary dark:!bg-transparent':
                  disabled,
              })}
              disabled={disabled}
              cellRender={customCell}
              ref={ref}
            />
          ) : (
            <DatePicker
              format={'DD/MM/YYYY'}
              placeholder="dd/mm/yyyy"
              id={id}
              disabledDate={resolvedDisabledDate}
              {...props}
              size="large"
              className={cn(inputBaseClasses, {
                '!border-red-500  ': status === 'error',
                '!border-green-500 ': status === 'success',
                'border-0 dark:border dark:!border-dark-text-placeholder !bg-light-button-label-primary dark:!bg-transparent':
                  disabled,
              })}
              disabled={disabled}
              cellRender={customCell}
              ref={ref}
            />
          )}
        </ConfigProvider>
        {helperText && (
          <HelperText text={helperText} disabled={disabled} status={status} />
        )}
      </div>
    );
  }
);

CustomDatePicker.displayName = 'CustomDatePicker';

export default CustomDatePicker;
